import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, Loader2, RefreshCw, Search } from "lucide-react";

interface Row {
  id: string;
  transaction_number: string | null;
  sender_id: string | null;
  recipient_id: string;
  sender_name: string | null;
  recipient_name: string | null;
  crypto_symbol: string;
  amount: number;
  source_type: string;
  created_at: string;
  sender_numeric?: string | null;
  recipient_numeric?: string | null;
  sender_email?: string | null;
  recipient_email?: string | null;
}

const AdminInternalTransfers = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("internal_transfers")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    const list = (data || []) as Row[];
    const ids = Array.from(
      new Set(list.flatMap((r) => [r.sender_id, r.recipient_id]).filter(Boolean) as string[]),
    );
    if (ids.length) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, numeric_id, email, full_name")
        .in("user_id", ids);
      const map = new Map((profs || []).map((p: any) => [p.user_id, p]));
      list.forEach((r) => {
        const s = r.sender_id ? map.get(r.sender_id) : null;
        const rc = map.get(r.recipient_id);
        r.sender_numeric = s?.numeric_id || null;
        r.sender_email = s?.email || null;
        if (!r.sender_name) r.sender_name = s?.full_name || s?.email || null;
        r.recipient_numeric = rc?.numeric_id || null;
        r.recipient_email = rc?.email || null;
        if (!r.recipient_name) r.recipient_name = rc?.full_name || rc?.email || null;
      });
    }
    setRows(list);
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      r.transaction_number?.toLowerCase().includes(s) ||
      r.crypto_symbol.toLowerCase().includes(s) ||
      r.sender_name?.toLowerCase().includes(s) ||
      r.recipient_name?.toLowerCase().includes(s) ||
      r.sender_numeric?.includes(s) ||
      r.recipient_numeric?.includes(s) ||
      r.sender_email?.toLowerCase().includes(s) ||
      r.recipient_email?.toLowerCase().includes(s)
    );
  });

  return (
    <Card className="glass">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <ArrowLeftRight className="w-5 h-5 text-primary" />
            سجل التحويلات الداخلية ({filtered.length})
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchRows}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث برقم العملية، الاسم، ID، أو البريد الإلكتروني..."
            className="pr-10 bg-secondary/60 border-0 rounded-xl"
          />
        </div>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-6 text-muted-foreground text-sm">لا توجد تحويلات داخلية</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((r) => (
              <div
                key={r.id}
                className="p-3 rounded-lg bg-secondary/40 border border-border/40 space-y-1"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-foreground">
                      {r.amount} {r.crypto_symbol}
                    </span>
                    {r.source_type === "platform" && (
                      <Badge className="bg-primary/20 text-primary text-[10px]">أرباح منصة</Badge>
                    )}
                  </div>
                  {r.transaction_number && (
                    <span className="text-xs font-mono text-primary">#{r.transaction_number}</span>
                  )}
                </div>
                <p className="text-xs text-foreground">
                  <span className="text-muted-foreground">المرسل:</span>{" "}
                  {r.sender_id ? (
                    <>
                      {r.sender_name || "-"}{" "}
                      <span className="font-mono text-primary" dir="ltr">
                        (ID: {r.sender_numeric || "-"})
                      </span>
                    </>
                  ) : (
                    <span className="text-primary">منصة CrebtoPay</span>
                  )}
                </p>
                <p className="text-xs text-foreground">
                  <span className="text-muted-foreground">المستقبل:</span>{" "}
                  {r.recipient_name || "-"}{" "}
                  <span className="font-mono text-primary" dir="ltr">
                    (ID: {r.recipient_numeric || "-"})
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString("ar-EG", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminInternalTransfers;
